/*
 * Users service
 * <p>
 *
 * @author lachezar.parvin
 * @author Gallagher Bassett IT
 * @author Copyright (c) 2020 Gallagher Bassett Services, Inc.
 *
 */

import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpParams
} from '@angular/common/http';
import {
  BehaviorSubject,
  Observable,
  Subject,
} from 'rxjs';
import { User } from 'app/shared/models/user.model';
import { map } from 'rxjs/operators';
import { Constants } from 'app/shared/config/constants';
import { CookieService } from 'ngx-cookie-service';
import { ConfigurationService } from 'app/shared/services/configuration.service';
import { TokenService } from 'app/shared/services/token.service';

@Injectable ( {
  providedIn: 'root',
} )
export class UserService {

  clientChanged = new Subject<any> ();
  canChangeClient = new BehaviorSubject ( true );
  readonly url = Constants.usersServiceUrl;
  readonly verificationCodeSendUrl = Constants.verificationCodeSendUrl;
  readonly verificationCodeCheckUrl = Constants.verificationCodeCheckUrl;
  private userDetails: any;
  private clientDetails: any;
  private isSystem: boolean;

  constructor (
    private http: HttpClient,
    private cookieService: CookieService,
    private configService: ConfigurationService,
    private tokenService: TokenService,
  ) {
  }

  /**
   * Current user details getter
   */
  get currentUser (): User {
    const storedUser = localStorage.getItem ( 'reporterUser' );

    return storedUser ? JSON.parse ( storedUser ) as User : {
      userId: '',
      name: '',
      email: '',
      clientAccessibility: [],
      settings: [],
    };
  }

  /**
   * Current user clients getter
   */
  get userClients (): any[] {
    return ( this.currentUser.clientAccessibility )
      .map ( a => {
        return {
          clientNumber: a.clientNumber || a.client,
          clientName: a.clientName || a.client,
          locationIds: a.locations || a.locationIds,
        };
      } );
  }

  set userClients ( clients: any[] ) {
    const details = this.currentUser;
    details.clientAccessibility = clients.map ( c => {
      return {
        clientNumber: c.clientNumber,
        clientName: c.clientName,
        locationIds: c.locationIds,
      };
    } );
    localStorage.setItem ( 'reporterUser', JSON.stringify ( details ) );
    this.userDetails = { ...details };
  }

  /**
   * Get locationIds of the currently select client in a comma separated list
   */
  get currentClientLocationIds (): string {
    if ( this.currentClient?.locationIds?.length ) {
      return this.currentClient.locationIds.join ( ',' );
    }
    return this.currentClient?.clientNumber;
  }

  /**
   * Current user settings getter
   */
  get userSettings (): any {
    const settings = {};
    this.currentUser.settings.forEach ( s => {
      settings[ s.key ] = s.value;
    } );
    return settings;
  }

  /**
   * Current user permissions getter
   */
  get userPermissions (): string[] {
    return this.tokenService.reporterUserPermissions;
  }

  /**
   * Current user has permission
   */
  userHasPermission ( permission: string ): boolean {
    return !!this.userPermissions?.includes ( permission );
  }

  /**
   * Current user workflow getter
   */
  get userWorkflow (): string {
    return this.tokenService.reporterUserWorkflow;
  }

  /**
   * Current user has workflow
   */
  userHasWorkflow ( workflow: string ): boolean {
    return this.userWorkflow === workflow;
  }

  /**
   *
   * @param userId
   *
   * @returns Observable<User>
   */
  getUser ( userId: string ): Observable<User> {
    return this.http.get<User> ( `${ this.url }/${ userId }`, {} )
      .pipe (
        map ( user => {
          localStorage.setItem ( 'reporterUser', JSON.stringify ( user ) );
          this.userDetails = { ...user };
          return user;
        } ),
      );
  }

  /**
   *
   * @param userId
   *
   */
  unsetUser () {
    localStorage.removeItem ( Constants.localStorageKeys.reporterUser );
    localStorage.removeItem ( Constants.localStorageKeys.reporterCurrentClient );
    localStorage.removeItem ( Constants.localStorageKeys.reporterBusinessUnitsSettings );
    localStorage.removeItem ( Constants.localStorageKeys.reporterClientSelected );
    this.userDetails = undefined;
    this.clientDetails = undefined;
    this.isSystem = undefined;
  }

  /**
   * Set the current client, set the first one if none is passed
   */
  set currentClient ( client: any ) {
    if ( !this.canChangeClient.getValue () ) {
      return;
    }
    this.currentClientNumber = client?.clientNumber || null;
  }

  /**
   *
   *
   * @Returns the current client or the first of the user's clients
   *
   */
  get currentClient () {
    if ( this.clientDetails ) {
      return this.clientDetails;
    }
    const reporterCurrentClient = localStorage.getItem ( 'reporterCurrentClient' );
    if ( reporterCurrentClient ) {
      return JSON.parse ( reporterCurrentClient );
    }
    return this.userClients ? this.userClients[ 0 ] : null;
  }

  /**
   * Set the current client by clientNumber, set the first one if none is passed
   */
  set currentClientNumber ( clientNumber: string ) {
    if ( !this.canChangeClient.getValue () ) {
      return;
    }
    if ( clientNumber ) {
      const client = this.userClients.find ( c => c.clientNumber === clientNumber );
      if ( client ) {
        this.clientDetails = { ...client };
        localStorage.setItem ( 'reporterCurrentClient', JSON.stringify ( client ) );
      }
    } else {
      this.clientDetails = { ...this.userClients[ 0 ] };
      localStorage.setItem ( 'reporterCurrentClient', JSON.stringify ( this.userClients[ 0 ] ) );
    }
    this.clientChanged.next ( this.currentClient );
  }

  disableClientChange () {
    this.canChangeClient.next ( false );
  }

  enableClientChange () {
    this.canChangeClient.next ( true );
  }

  loginAdminWithGet ( accessToken: string ) {
    if ( this.userHasPermission ( 'ADMIN_USER' ) ) {
      const form = document.createElement ( 'form' );
      form.id = 'admin-redirect';
      form.method = 'GET';
      form.action = `${ this.configService.byKey ( 'admin.redirect-auth-url' ) }/${ accessToken }`;
      document.body.appendChild ( form );
      form.submit ();
      form.remove ();
    }
  }

  loginAdmin ( accessToken: string | Promise<string>, self = false ) {
    if ( this.userHasPermission ( 'ADMIN_USER' ) || this.userHasPermission ( 'LIMITED_ADMIN' ) ) {
      this.setHandOffCookie ( accessToken as string );
      window.open ( this.configService.byKey ( 'admin.redirect-auth-url' ), self ? '_self' : 'ReporterAdmin' );
    }
  }

  handOffToApp ( accessToken: string, authRedirectApp: string ) {
    this.setHandOffCookie ( accessToken );
    window.open ( this.configService.byKey ( Constants.authRedirectAppLandingUrlKeys.get ( authRedirectApp ) ), '_self' );
  }

  private setHandOffCookie ( accessToken: string ) {
    this.cookieService.set ( 'token', accessToken, {
      path: Constants.handOffCookiePath,
      domain: this.handOffCookieDomain,
    } );
  }

  private get handOffCookieDomain (): string | undefined {
    const host = this.currentHost;
    const domain = ( ( this.configService.byKey ( 'cookie.domain' ) as string ) ?? '' )
      .split ( ',' )
      .map ( d => d.trim().replace ( /^\./, '' ) )
      .find ( d => !!d && ( host === d || host.endsWith ( `.${ d }` ) ) );

    // The browser rejects a cookie for a domain the page is not under (for example on localhost).
    // A leading dot is set explicitly so the cookie is shared with every subdomain (e.g. chat.<domain>)
    // rather than being host-only.
    return domain ? `.${ domain }` : undefined;
  }

  private get currentHost (): string {
    return location.hostname;
  }

  async addTempClient ( client ) {
    const details = this.currentUser;
    if ( !details.clientAccessibility?.find ( c => c.clientNumber === client.clientNumber ) ) {
      details.clientAccessibility?.push ( client );
      this.userDetails = details;
      await localStorage.setItem ( 'reporterUser', JSON.stringify ( details ) );
    }
  }

  updateUserAccessibility ( clientAccessibility: any[] ) {
    const details = this.currentUser;
    details.clientAccessibility = clientAccessibility;
    localStorage.setItem ( Constants.localStorageKeys.reporterUser, JSON.stringify ( details ) );
    this.userDetails = { ...details };
    return details;
  }

  updateUserEmail ( email: string ) {
    const details = this.currentUser;
    details.email = email;
    localStorage.setItem ( Constants.localStorageKeys.reporterUser, JSON.stringify ( details ) );
    this.userDetails = { ...details };
  }

  get isSystemUser (): boolean {
    if ( this.isSystem === undefined ) {
      this.isSystem = this.userClients.find ( c => c.clientNumber === 'SYSTEM' ) !== undefined;
    }
    return this.isSystem;
  }

  sendVerificationCodeForToken ( token: string ): Observable<any> {
    return this.http.post<any> ( `${ this.verificationCodeSendUrl }?resetToken=${ token }`, null );
  }

  sendVerificationCodeForUserSearchTerm ( userSearchTerm: string ): Observable<any> {
    const params = new HttpParams ()
      .set ( 'userSearchTerm', userSearchTerm )
      .set ( 'requireResetToken', 'false' );
    return this.http.post<any> ( this.verificationCodeSendUrl, null, { params } );
  }

  checkVerificationCode ( code: string, userId: string ): Observable<any> {
    return this.http.post<any> ( `${ this.verificationCodeCheckUrl }?userId=${ userId }`, code );
  }

  setNewPassword ( userId, token: string, allowActivePasswordReset: boolean, password: string, passwordConfirmation: string ): Observable<any> {
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${ token }`,
    };
    const params = new HttpParams ()
      .set ( 'allowActivePasswordReset', allowActivePasswordReset );
    return this.http.post<any> ( `${ this.url }/${ userId }/password-reset`, {
      password,
      passwordConfirmation,
    }, {
      headers,
      params
    } );
  }
}


